import { z } from 'zod';
/**
 * Идентификатор любой доменной строки. UUID v7: сортируется по времени.
 *
 * ПОЧЕМУ ПРИВОДИТСЯ К СТРОЧНЫМ. UUID принимается в любом регистре, а PostgreSQL отдаёт
 * его строчным — и каждое сравнение строк в коде («родитель — это сама категория»,
 * «передать записи самому себе», «тип ресурса указан дважды», «филиал из списка
 * доступных») промахивалось на том же id, написанном заглавными: самопередача
 * проходила, кольцо в дереве категорий доходило до триггера безликим 422.
 * Приведение здесь — одно место на все контракты, а не проверка в каждом сравнении.
 * toLowerCase(), а не transform(): схема остаётся строкой uuid, и OpenAPI из неё
 * по-прежнему строится (преобразование z.toJSONSchema представить не может).
 */
export const Uuid = z.uuid().toLowerCase();
/** Идентификатор тенанта (салона-клиента платформы). Строчными — по той же причине, что Uuid. */
export const TenantId = z.uuid().toLowerCase();
/**
 * Публичный идентификатор салона в адресах: <slug>.planerius.ru, /s/<slug> в Mini App.
 * Витрина и боты знают салон только по нему — внутренние UUID наружу не уходят.
 */
export const TenantSlug = z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'допустимы строчные латинские буквы, цифры и дефис');
/**
 * Момент времени — всегда ISO-8601 со смещением.
 * Локальное время филиала API отдаёт отдельно, вместе с IANA-именем зоны:
 * показывать клиенту время салона, а не устройства.
 */
export const Instant = z.iso.datetime({ offset: true });
/**
 * Календарная дата без времени и без зоны: «15 октября».
 * Клиент выбирает именно её, а в какой интервал абсолютного времени она
 * разворачивается, решает PostgreSQL по зоне филиала (ADR-007 п.6).
 */
export const LocalDate = z.iso.date();
/**
 * Настенное время филиала «ЧЧ:ММ»: «открываемся в 9:00» остаётся девятью часами
 * и после смены зоны региона. Момента в этом значении нет и быть не должно.
 */
export const LocalTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'ожидается настенное время вида 09:30');
/** Деньги — только целые копейки, как в схеме (поля *Minor): Float и Decimal в домене запрещены. */
export const MinorAmount = z.number().int().nonnegative();
/**
 * Канонические имена зон из ICU среды исполнения; null — среда списка не отдаёт
 * (старый движок в приложении). Считается один раз: список не меняется до перезапуска.
 */
let supportedZones;
function isKnownTimeZone(name) {
    if (name === 'UTC') {
        return true;
    }
    if (typeof Intl === 'undefined') {
        // Проверить нечем — решит сервер: база филиала сама не примет неизвестную зону.
        return true;
    }
    if (supportedZones === undefined) {
        supportedZones =
            typeof Intl.supportedValuesOf === 'function' ? new Set(Intl.supportedValuesOf('timeZone')) : null;
    }
    if (supportedZones?.has(name) === true) {
        return true;
    }
    // Список ICU хранит одно имя на зону, и не всегда новое: в Node 26 там Europe/Kiev
    // и Asia/Calcutta, а Europe/Kyiv и Asia/Kolkata нет. Обе пары — законные имена IANA,
    // PostgreSQL знает оба, а браузер пользователя отдаёт то, что знает сам. Поэтому
    // имя вне списка ещё раз спрашиваем у Intl напрямую: синоним он примет, выдуманную
    // зону — нет (RangeError).
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone: name }).resolvedOptions().timeZone !== '';
    }
    catch {
        return false;
    }
}
/**
 * IANA-имя часового пояса, например Europe/Moscow. Числовое смещение не храним.
 *
 * Одной формы «Область/Город» мало: «Europe/Atlantis» её проходит, дальше
 * отказывает CHECK филиала в базе, и человек вместо «такой зоны нет» получал 500.
 * А там, где CHECK нет, выдуманная зона сохранялась бы молча и роняла потом каждый
 * расчёт — `AT TIME ZONE` с неизвестным именем это ошибка. Поэтому выдуманные имена
 * отсекает сам контракт, до базы.
 *
 * Последнее слово при этом не за контрактом. Проверка идёт по ICU среды исполнения,
 * а ICU помнит синонимы, которые tzdata PostgreSQL уже выбросила (US/Pacific-New,
 * Canada/East-Saskatchewan): Intl их принимает, база — нет. Каноническое имя тут
 * не помогает — Intl сводит их к живым America/Los_Angeles и America/Regina. Поэтому
 * там, где зона сохраняется, её ещё раз спрашивает сервер у самой базы (филиал —
 * settings.service.ts, той же функцией planerius_is_valid_timezone, что стоит в CHECK).
 */
export const TimeZone = z
    .string()
    .regex(/^(?:[A-Za-z]+\/[A-Za-z_+-]+|UTC)$/, 'ожидается IANA-имя зоны')
    .refine(isKnownTimeZone, 'такого часового пояса нет в базе IANA')
    // Пример для openapi.json: из регулярного выражения генератор его не выводит.
    .meta({ examples: ['Europe/Moscow'] });
/** Телефон в формате E.164 — единственный формат хранения. */
export const Phone = z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, 'ожидается формат +79991234567')
    .meta({ examples: ['+79991234567'] });
/**
 * Язык интерфейса. Мультиязычность заложена с первого дня (решение владельца 26).
 *
 * КАЗАХСКИЙ ОБЪЯВЛЕН РАНЬШЕ ПЕРЕВОДОВ, И ЭТО НЕ ЗАДЕЛ НА БУДУЩЕЕ. Язык человека
 * хранится в его учётке и попадает в шаблоны сообщений: добавить значение в enum
 * задним числом — это миграция базы, правка контракта и разбор того, что делать
 * с уже накопленными строками. Объявить сразу дешевле. А вот ПРЕДЛАГАТЬ язык,
 * для которого нет переводов, нельзя: переключатель в кабинете показывает только
 * те языки, у которых словарь готов (см. apps/cabinet/src/i18n).
 */
export const Locale = z.enum(['ru', 'en', 'kk']);
/** Постраничная выдача: курсор, а не смещение — списки растут и смещение поедет. */
export const PageQuery = z.object({
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(20),
});
export const problemDetails = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    detail: z.string().optional(),
    instance: z.string().optional(),
});
//# sourceMappingURL=common.js.map